import { useEffect, useState } from 'react';

interface KaraokeHeroProps {
  lang: 'en' | 'ja';
}

export default function KaraokeHero({ lang }: KaraokeHeroProps) {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const content = {
    en: {
      badge: 'New Experience',
      title: 'Tokyo Karaoke Night',
      subtitle: 'No stress. Just fun.',
      description: 'Enjoy Japanese karaoke like you\'re with a local friend. We\'ll guide you through everything.',
      cta: 'Book via WhatsApp',
      scroll: 'Scroll to explore'
    },
    ja: {
      badge: '新サービス',
      title: 'Tokyo Karaoke Night',
      subtitle: 'ストレスなし。ただ楽しむだけ。',
      description: '地元の友人と一緒にいるように、日本のカラオケを楽しみましょう。すべてサポートします。',
      cta: 'WhatsAppで予約',
      scroll: 'スクロールして詳細を見る'
    }
  };

  const t = content[lang];

  const handleWhatsAppClick = () => {
    const message = lang === 'en' 
      ? 'Hi! I\'d like to book Tokyo Karaoke Night experience.'
      : 'こんにちは！Tokyo Karaoke Nightの予約をしたいです。';
    window.open(`https://wa.me/818041800418?text=${encodeURIComponent(message)}`, '_blank');
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/0bbd2e958159f92ebe84dcf688809679.jpg"
          alt="Tokyo Karaoke Night"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-purple-900/70 via-pink-900/60 to-black/80"></div>
      </div>

      {/* Animated Neon Circles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-64 h-64 bg-pink-500/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-80 h-80 bg-purple-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-5xl mx-auto px-6 py-32 text-center">
        {/* Badge */}
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-pink-500/20 to-purple-500/20 backdrop-blur-sm border border-pink-400/30 rounded-full mb-8">
          <div className="w-2 h-2 bg-pink-400 rounded-full animate-pulse"></div>
          <span className="text-pink-200 text-sm font-medium tracking-wide">{t.badge}</span>
        </div>

        {/* Title */}
        <h1 className="text-6xl md:text-7xl lg:text-8xl font-black mb-6 leading-tight">
          <span className="bg-gradient-to-r from-pink-400 via-purple-400 to-blue-400 bg-clip-text text-transparent drop-shadow-2xl">
            {t.title}
          </span>
        </h1>

        {/* Subtitle */}
        <p className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 drop-shadow-lg">
          {t.subtitle}
        </p>

        {/* Description */}
        <p className="text-lg md:text-xl text-gray-200 max-w-2xl mx-auto mb-12 leading-relaxed">
          {t.description}
        </p>

        {/* CTA Button */}
        <button
          onClick={handleWhatsAppClick}
          className="group relative inline-flex items-center gap-3 px-10 py-5 bg-gradient-to-r from-pink-500 to-purple-600 text-white text-lg font-bold rounded-full shadow-2xl hover:shadow-pink-500/50 transition-all duration-300 hover:scale-105 whitespace-nowrap"
        >
          <i className="ri-whatsapp-line text-2xl"></i>
          <span>{t.cta}</span>
          <i className="ri-arrow-right-line text-xl group-hover:translate-x-1 transition-transform"></i>
        </button>

        {/* Scroll Indicator */}
        <div className={`absolute bottom-12 left-1/2 -translate-x-1/2 transition-opacity duration-500 ${scrolled ? 'opacity-0' : 'opacity-100'}`}>
          <div className="flex flex-col items-center gap-2 text-white/60">
            <span className="text-sm font-medium">{t.scroll}</span>
            <i className="ri-arrow-down-line text-2xl animate-bounce"></i>
          </div>
        </div>
      </div>
    </section>
  );
}